import { ChatChannels } from '@core/chat';
import { PollResults } from '@core/poll';
import { PresentationSlides } from '@core/presentation-panel';
import { AuthContext } from '@core/shared';
import { ClientType, LiveStatus, WebinarStatus } from '@core/types';
import { ErrorMessagePage, Loader, useLayout } from '@core/ui';
import { AccountWizardPage } from '@online-webinar/account';
import { useFullscreenController, VindralPlayer } from '@online-webinar/player';
import { useParams } from '@online-webinar/router';
import { joinWebinar, leaveWebinar, useAppDispatch, useAppSelector } from '@online-webinar/store';
import { RefObject, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { EventEndedInfo } from '../..';
import { PollQuestion } from '../../atoms/PollQuestion';
import { VideoContent, WebinarBasicInfo } from '../../molecules';
import { BroadcastWebinarHeader, ModeratorLayout, ParticipantList } from '../../organism';

export const Moderator = () => {
    const { t } = useTranslation();
    const { webinarId, linkCode } = useParams({ strict: false });
    const dispatch = useAppDispatch();
    const isAuthenticated = useContext(AuthContext).isAuthenticated;

    const videoContainerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement | null>(null);

    const { mainContainerRef } = useLayout();

    useEffect(() => {
        document.body.classList.add('no-menu');

        if (!isAuthenticated) {
            return;
        }

        dispatch(joinWebinar({ webinarId: webinarId!, authenticationCode: linkCode!, clientType: ClientType.moderator }));
        return () => {
            document.body.classList.remove('no-menu');
            dispatch(leaveWebinar());
        };
    }, [isAuthenticated]);

    const isInitialized = useAppSelector((state) => state.liveWebinar.isInitialized);
    const webinarStatus = useAppSelector((state) => state.liveWebinar.webinarData.status);
    const slideDeck = useAppSelector((state) => state.liveWebinar.slideDeck);
    const activeSlideNumber = useAppSelector((state) => state.liveWebinar.activeSlideNumber);
    const dataConnection = useAppSelector((state) => state.liveWebinar.dataConnection);
    const activePoll = useAppSelector((state) => state.liveWebinar.activePoll);
    const { toggleFullscreen, isFauxFullscreen, isFullscreen } = useFullscreenController(videoContainerRef);

    if (!webinarId) {
        return <ErrorMessagePage statusCode={404} />;
    }

    if (!isAuthenticated) {
        return <AccountWizardPage isModerator={true} />;
    }

    if (dataConnection.status === LiveStatus.Error) {
        const error = dataConnection.error ?? { statusCode: 500, success: false };
        return <ErrorMessagePage apiError={error} />;
    }

    if (!isInitialized) {
        return <Loader classNames="m-auto" />;
    }

    return (
        <>
            <ModeratorLayout ref={mainContainerRef}>
                <BroadcastWebinarHeader>
                    <WebinarBasicInfo />
                    <h1 className="bg-dashed-line-vert bg-[length:1px_8px] bg-repeat-y text-primary flex items-center text-xl sm:text-2xl font-medium font-accent ml-4 mr-auto pl-4">
                        Moderator
                    </h1>
                </BroadcastWebinarHeader>
                <div
                    ref={videoContainerRef}
                    className={`${isFauxFullscreen ? 'fixed inset-0 bg-black z-50' : 'relative max-h-[320px]'} col-start-1 row-start-2`}
                >
                    <VideoContent>
                        <VindralPlayer isFullscreen={isFullscreen} isFauxFullscreen={isFauxFullscreen} toggleFullscreen={toggleFullscreen} />
                        {webinarStatus === WebinarStatus.Ended && <EventEndedInfo />}
                    </VideoContent>
                </div>
                <div className="col-start-1 row-start-2 bc:row-start-3 min-h-0">
                    <PresentationSlides
                        slides={slideDeck?.slides ?? []}
                        currentSlideNumber={activeSlideNumber ?? 0}
                        classNames="object-top"
                        showFullScreen="false"
                    />
                </div>
                <div className="col-start-1 bc:col-start-2 row-start-2 bc:row-span-2 min-h-0">
                    <ChatChannels />
                </div>
                <div className="col-start-1 bc:col-start-3 row-start-2 bc:row-span-2 min-h-0 flex flex-col">
                    {activePoll && (
                        <>
                            {!activePoll.publicResults && <>{t('Broadcaster.Poll.NoPublicResults')}</>}
                            <PollQuestion classNames="border-b border-primary text-lg p-2" question={activePoll.question} />
                            <PollResults poll={activePoll} />
                            {activePoll.applyWeighting && <p className="bg-info rounded-lg text-sm mb-2 mx-2 p-2">{t('Broadcaster.Poll.WeightingApplied')}</p>}
                        </>
                    )}
                    <ParticipantList />
                </div>
            </ModeratorLayout>
        </>
    );
};
